import { useRef, useEffect, useState } from 'react';
import { flip, shift, offset, arrow, autoUpdate, useFloating } from '@floating-ui/react';
import { getEditingAreaAttr } from '@/components/Editor/hooks/useEditingAreas';
import { elementFromPoint } from '@/utils/dom'; // Default size of tooltip to avoid flip issues

const DEFAULT_SIZE = 44;

const useEditingAreaPosition = _ref => {
  let {
    activeSectionId,
    activeSectionEditingArea,
    activeClickPosition,
    nodeId,
    placement = 'bottom',
    leftPositionOffset = 0,
    topPositionOffset = 0,
    shift: shiftEnabled = true
  } = _ref;
  const arrowRef = useRef(null);
  const {
    0: referenceElement,
    1: setReferenceElement
  } = useState(null);
  const {
    refs,
    elements,
    floatingStyles,
    context
  } = useFloating({
    nodeId,
    placement: placement,
    whileElementsMounted: autoUpdate,
    elements: {
      reference: referenceElement
    },
    middleware: [offset(-12), flip(), shiftEnabled ? shift() : undefined, arrow({
      element: arrowRef.current
    })]
  }); // Update position of tooltip as edit area changes

  useEffect(() => {
    var _refs$floating;

    if (!activeSectionId || typeof (activeClickPosition === null || activeClickPosition === void 0 ? void 0 : activeClickPosition.x) !== 'number' || typeof (activeClickPosition === null || activeClickPosition === void 0 ? void 0 : activeClickPosition.y) !== 'number') {
      return;
    }

    const floatingEl = (refs === null || refs === void 0 ? void 0 : (_refs$floating = refs.floating) === null || _refs$floating === void 0 ? void 0 : _refs$floating.current) || null; // Hide tooltip to get correct element from point

    if (floatingEl) {
      floatingEl.style.display = 'none';
    } // Lookup nearest element clicked


    const clickEl = elementFromPoint(activeClickPosition.x, activeClickPosition.y);
    if (!clickEl) return;

    if (floatingEl) {
      floatingEl.style.display = 'block';
    } // Lookup any parent edit area


    const editAreaEl = activeSectionEditingArea ? clickEl.closest(`[${getEditingAreaAttr()}]`) : null; // Prioritize discovered editible areas by
    // setting them as the reference element
    // Falling back to original clicked position

    if (editAreaEl) {
      setReferenceElement(editAreaEl);
    } else {
      const initialRect = clickEl.getBoundingClientRect();
      const initialClickElTop = initialRect.top || 0;
      const initialClickElLeft = initialRect.left || 0;
      refs.setPositionReference({
        getBoundingClientRect() {
          const rect = clickEl.getBoundingClientRect(); // Adjust tooltip position on scroll

          const clickElTop = rect.top || 0;
          const topOffset = initialClickElTop - clickElTop;
          const clickElLeft = rect.left || 0;
          const leftOffset = initialClickElLeft - clickElLeft;
          return DOMRect.fromRect({
            width: DEFAULT_SIZE,
            height: DEFAULT_SIZE,
            x: activeClickPosition.x - leftOffset - DEFAULT_SIZE / 2 - leftPositionOffset,
            y: activeClickPosition.y - topOffset - DEFAULT_SIZE / 2 - topPositionOffset
          });
        },

        contextElement: clickEl
      });
    } // Cleanup


    return () => {
      setReferenceElement(null);
      refs.setPositionReference(null);
    };
  }, [activeSectionId, activeClickPosition, activeSectionEditingArea, leftPositionOffset, topPositionOffset, refs]);
  return {
    floatingStyles,
    arrowRef,
    context,
    elements,
    refs,
    setReferenceElement
  };
};

export default useEditingAreaPosition;